import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import {
  LucideAngularModule,
  LucideIconProvider,
  LUCIDE_ICONS,
  Globe,
  Settings,
  Shield,
  User,
  LogOut,
  Menu,
  X,
} from 'lucide-angular';
import { AuthService } from '@core/auth/auth.service';

@Component({
  selector: 'app-nav-bar',
  imports: [RouterLink, RouterLinkActive, LucideAngularModule],
  providers: [
    {
      provide: LUCIDE_ICONS,
      multi: true,
      useValue: new LucideIconProvider({ Globe, Settings, Shield, User, LogOut, Menu, X }),
    },
  ],
  templateUrl: './nav-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavBarComponent {
  protected readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly userMenuOpen = signal(false);
  protected readonly mobileMenuOpen = signal(false);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private readonly BASE_BTN =
    'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2]';

  protected readonly horizonActive = computed(() =>
    this.currentUrl().startsWith('/regulate'),
  );

  protected readonly horizonLinkClass = computed(() =>
    `${this.BASE_BTN} ${this.horizonActive() ? 'bg-[#4d806f] text-white' : 'bg-[#73716d] text-[#faf6ed] hover:bg-[#4d806f]'}`,
  );

  @HostListener('document:keydown.escape')
  protected closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  protected toggleMobileMenu(): void {
    this.mobileMenuOpen.update(v => !v);
  }

  protected onLogout(): void {
    this.userMenuOpen.set(false);
    this.mobileMenuOpen.set(false);
    this.auth.logout();
  }
}
